import React, { useMemo, useState } from "react";
import axios from "axios";
import { Download, FileSpreadsheet, Loader2, Mail, Printer } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SupplierPicker } from "./SupplierPicker";
import { DerivedPrintSheet } from "./DerivedPrintSheet";
import { derivedExportTable } from "./derivedExport";

export const DerivedExportToolbar = ({ tab, sheet, suppliers, setSuppliers, canDispatch, dirty, onSheetUpdate }) => {
  const [supplierId, setSupplierId] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const table = useMemo(() => derivedExportTable(tab, sheet.sections, sheet.scratchpad_settings), [tab, sheet.sections, sheet.scratchpad_settings]);
  const prefix = `${tab}-export`;
  const query = new URLSearchParams({ tab, ...(supplierId ? { supplier_id: supplierId } : {}) });

  const print = () => {
    if (!table.rows.length) return toast.error(`No ${tab} rows to print`);
    const cleanup = () => document.body.classList.remove("derived-ledger-printing");
    document.body.classList.add("derived-ledger-printing");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
    window.setTimeout(cleanup, 1500);
  };
  const download = extension => {
    if (!table.rows.length) return toast.error(`No ${tab} rows to export`);
    if (dirty) return toast.error("Wait for the sheet to finish saving");
    window.open(`${API}/production-sheets/${sheet.sheet_id}/derived-export.${extension}?${query.toString()}`, "_blank", "noopener,noreferrer");
  };
  const email = async () => {
    if (!supplierId) return toast.error("Choose a supplier");
    if (!table.rows.length) return toast.error(`No ${tab} rows to email`);
    if (dirty) return toast.error("Wait for the sheet to finish saving");
    setSending(true);
    try {
      const response = await axios.post(`${API}/production-sheets/${sheet.sheet_id}/derived-dispatch`, { supplier_id: supplierId, tab, notes: notes || null });
      toast.success(response.data.dispatch.email_sent ? `${table.title} emailed` : "Dispatch recorded — SMTP did not send");
      onSheetUpdate?.(response.data.sheet);
      setNotes("");
    } catch (error) { toast.error(error.response?.data?.detail || `Could not email ${tab} PDF`); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-2 rounded-md border border-slate-300 bg-white p-3" data-testid={`${prefix}-toolbar`}>
      <div className="grid gap-2 md:grid-cols-[minmax(220px,1fr)_minmax(220px,1fr)_auto]">
        <SupplierPicker suppliers={suppliers} value={supplierId} onChange={setSupplierId} disabled={!canDispatch} testId={`${prefix}-supplier-picker`}
          defaultCategory={tab === "finishing" ? "finishing" : "hardware"}
          onCreated={supplier => setSuppliers(previous => [...previous, supplier].sort((a, b) => a.company_name.localeCompare(b.company_name)))} />
        <Input value={notes} onChange={event => setNotes(event.target.value)} placeholder="Email note (optional)" data-testid={`${prefix}-email-note`} />
        <span className="self-center text-xs text-slate-500" data-testid={`${prefix}-row-count`}>{table.rows.length} line{table.rows.length === 1 ? "" : "s"}</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Button variant="outline" size="sm" onClick={print} disabled={!table.rows.length} data-testid={`${prefix}-print-button`}><Printer className="mr-1.5 h-4 w-4" />Print</Button>
        <Button variant="outline" size="sm" onClick={() => download("pdf")} disabled={!table.rows.length || dirty} data-testid={`${prefix}-pdf-button`}><Download className="mr-1.5 h-4 w-4" />Download PDF</Button>
        <Button variant="outline" size="sm" onClick={() => download("csv")} disabled={!table.rows.length || dirty} data-testid={`${prefix}-csv-button`}><FileSpreadsheet className="mr-1.5 h-4 w-4" />Export CSV</Button>
        <Button size="sm" onClick={email} disabled={!canDispatch || !supplierId || !table.rows.length || dirty || sending} data-testid={`${prefix}-email-button`}>{sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}Email PDF</Button>
      </div>
      <DerivedPrintSheet table={table} sheet={sheet} />
    </div>
  );
};